// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export type PdfSettingsState = {
    parameters: {
        withCoverPage?: boolean;
        withTableOfContent?: boolean;
        withAboutUs?: boolean;
        withSpecSheet?: boolean;
        productsInlineFormat?: boolean;
        customerEmailAddress?: string;
        preparedFor?: string;
        consultant?: string;
        projectName?: string;
        phone?: string;
        pdfType?: "quote" | "wishList";
        ThirdPartyQuoteClientName?: string;
        ThirdPartyQuoteClientStreetAddress1?: string;
        ThirdPartyQuoteClientStreetAddress2?: string;
        ThirdPartyQuoteClientCity?: string;
        ThirdPartyQuoteClientProvince?: string;
        ThirdPartyQuoteClientPostalCode?: string;
        ThirdPartyQuoteWebsite?: string;
        ThirdPartyQuoteClientEmail?: string;
        ThirdPartyQuoteClientPhoneNumber?: string;
        ThirdPartyQuoteFreightCharges?: number;
        ThirdPartyQuoteLabourCharges?: number;
        ThirdPartyQuoteTaxDisplayOption?: "Hst" | "Gst" | "NoTax";
        ThirdPartyQuotePriceDisplayOption?: "ShowProductPrices" | "HideProductPrices";
        ThirdPartyQuoteExpirationDate?: string | null;
        includePrice?: boolean;
        generalNotes?: string;
    };
};

const initialState: PdfSettingsState = {
    parameters: {
        withCoverPage: false,
        withTableOfContent: false,
        withAboutUs: false,
        withSpecSheet: false,
        productsInlineFormat: true,
        customerEmailAddress: "",
        preparedFor: "",
        consultant: "",
        projectName: "",
        phone: "",
        pdfType: "wishList",
        ThirdPartyQuoteClientName: "",
        ThirdPartyQuoteClientStreetAddress1: "",
        ThirdPartyQuoteClientStreetAddress2: "",
        ThirdPartyQuoteClientCity: "",
        ThirdPartyQuoteClientProvince: "",
        ThirdPartyQuoteClientPostalCode: "",
        ThirdPartyQuoteWebsite: "",
        ThirdPartyQuoteClientEmail: "",
        ThirdPartyQuoteClientPhoneNumber: "",
        ThirdPartyQuoteFreightCharges: 0,
        ThirdPartyQuoteLabourCharges: 0,
        ThirdPartyQuoteTaxDisplayOption: "NoTax",
        ThirdPartyQuotePriceDisplayOption: "ShowProductPrices",
        ThirdPartyQuoteExpirationDate: null,
        includePrice: true,
        generalNotes: "",
    },
};

const pdfSettings = createTypedReducerWithImmer(initialState, {
    "PdfSettings/UpdatePdfSettings": (draft: Draft<PdfSettingsState>, actions: { value: typeof draft.parameters }) => {
        draft.parameters = actions.value;
    },
    "PdfSettings/ClearSettings": (draft: Draft<PdfSettingsState>) => {
        draft.parameters = initialState.parameters;
    },
});

(reducers as any).pdfSettings = pdfSettings;

type pdfSettingsReducers = {
    pdfSettings: typeof pdfSettings;
};

export type pdfSettingsActions = Parameters<pdfSettingsReducers[keyof pdfSettingsReducers]>[1] | AnyAction;
